import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { createComment } from "../../services/commentService";
import { parseErrorMessage } from "../../services/api";
import { useAuth } from "../../context/AuthContext";

const CommentForm = ({ postId, onCommentSubmit, inputRef }) => {
    const [comment, setComment] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated } = useAuth() || {};

    const textareaRef = useRef(null);

    useEffect(() => {
        if (!isAuthenticated) return;
        if (textareaRef.current) {
            textareaRef.current.focus({ preventScroll: false });
            autoResize(textareaRef.current);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (!inputRef) return undefined;
        if (typeof inputRef === "function") {
            inputRef(textareaRef.current);
            return () => inputRef(null);
        }
        if (typeof inputRef === "object") {
            inputRef.current = textareaRef.current;
            return () => {
                inputRef.current = null;
            };
        }
        return undefined;
    }, [inputRef]);

    const autoResize = (textarea) => {
        if (!textarea) return;
        textarea.style.height = "auto";
        textarea.style.height = `${Math.min(textarea.scrollHeight, 240)}px`;
    };

    const submitComment = async () => {
        const trimmed = comment.trim();
        if (!trimmed) {
            return;
        }

        if (!isAuthenticated) {
            navigate("/login", { state: { from: location.pathname } });
            return;
        }

        setSubmitting(true);
        setErrorMessage("");

        try {
            const commentData = { content: trimmed };
            const newComment = await createComment(postId, commentData);
            onCommentSubmit(newComment);
            setComment("");
            if (textareaRef.current) {
                textareaRef.current.style.height = "auto";
                textareaRef.current.focus({ preventScroll: false });
            }
        } catch (error) {
            console.error("Failed to create comment:", error);
            setErrorMessage(parseErrorMessage(error));
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        await submitComment();
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            submitComment();
            return;
        }

        if ((e.key === "Enter" && (e.ctrlKey || e.metaKey)) || (e.key === "Enter" && e.shiftKey)) {
            const textarea = textareaRef.current;
            if (!textarea) return;
            const { selectionStart, selectionEnd, value } = textarea;
            const newValue = `${value.slice(0, selectionStart)}\n${value.slice(selectionEnd)}`;
            setComment(newValue);
            requestAnimationFrame(() => {
                if (textareaRef.current) {
                    const cursor = selectionStart + 1;
                    textareaRef.current.selectionStart = cursor;
                    textareaRef.current.selectionEnd = cursor;
                    autoResize(textareaRef.current);
                }
            });
            e.preventDefault();
        }
    };

    if (!isAuthenticated) {
        return (
            <div className="comment-auth-cta">
                <p className="comment-auth-text">
                    登入後即可留下留言，分享你的夜晚故事。
                </p>
                <button
                    type="button"
                    className="btn primary"
                    onClick={() => navigate("/login", { state: { from: location.pathname } })}
                >
                    🔓 立即登入
                </button>
            </div>
        );
    }

    return (
        <form className="comment-form" onSubmit={handleSubmit}>
            {errorMessage && <p className="comment-error">{errorMessage}</p>}
            <label className="visually-hidden" htmlFor="comment-input">
                留言內容
            </label>
            <textarea
                id="comment-input"
                className="comment-textarea"
                ref={textareaRef}
                value={comment}
                onChange={(e) => {
                    setComment(e.target.value);
                    autoResize(e.target);
                }}
                onKeyDown={handleKeyDown}
                placeholder="分享你的想法..."
                required
                rows={3}
            />
            <div className="form-actions">
                <button type="submit" className="btn primary" disabled={submitting}>
                    {submitting ? "…發佈中" : "✨ 發佈留言"}
                </button>
            </div>
        </form>
    );
};

export default CommentForm;
